// frontend/src/components/NotificationSettings.js
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from '../axiosConfig';
import { Bell } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedCard } from './themed';
import { usePreferences } from '../contexts/PreferenceContext';
import notificationService from '../services/notificationService';
import UserNotificationPreferences from './notifications/UserNotificationPreferences';

/**
 * User notification settings component
 * Provides user interface for managing personal notification preferences
 */
const NotificationSettings = () => {
    const { t } = useTranslation();
    const { background, text, cn } = useTheme();
    const { language } = usePreferences();
    const [loading, setLoading] = useState(true);
    const [availableChannels, setAvailableChannels] = useState({});
    const [eventTypes, setEventTypes] = useState([]);
    const [visibilityData, setVisibilityData] = useState(null);

    const entityId = localStorage.getItem('entityId');
    

    useEffect(() => {
        if (entityId) {
            loadNotificationData();
        }
    }, [entityId, language]);

    /**
     * Load user notification data and settings
     */
    const loadNotificationData = async () => {
        try {
            setLoading(true);

            if (!entityId) {
                console.error('No entityId found');
                return;
            }

            // Get menu visibility data from the new API
            const visibility = await notificationService.checkMenuVisibility(entityId);
            setVisibilityData(visibility);

            // Get entity channels for users
            const entityChannels = await notificationService.getEntityChannels(entityId);
            const entityMap = {};
            for (const ch of entityChannels) {
                entityMap[ch.channel] = (ch.is_enabled === 1 || ch.is_enabled === true);
            }

            const systemChannels = visibility.globalChannels || visibility.channels || {};

            // Build available channels object for user preferences
            const userAvailableChannels = {};
            for (const [channel, isGloballyActive] of Object.entries(systemChannels)) {
                const isEntityEnabled = entityMap[channel] === true;
                // Channel is available for user preferences if it's globally active AND entity-enabled
                userAvailableChannels[channel] = isGloballyActive && isEntityEnabled;
            }

            setAvailableChannels(userAvailableChannels);

            // Load event types
            await loadEventTypes();

        } catch (error) {
            console.error('Failed to load notification settings:', error);
            toast.error(t('errorLoadingNotificationSettings'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Load event types filtered by available channels
     */
    const loadEventTypes = async () => {
        try {
            const eventsRes = await axios.get(`/api/notification-settings/${entityId}/event-types`);
            if (eventsRes.data.success) {
                const events = eventsRes.data.data.map(event => ({
                    ...event,
                    displayName: t(event.display_name_key),
                    description: t(event.description_key)
                }));
                setEventTypes(events);
            }
        } catch (error) {
            console.error('Error loading event types:', error);
        }
    };

    /**
     * Handle settings change callback
     */
    const handleSettingsChange = () => {
        loadNotificationData();
    };

    // Check if user has any available notification options
    const hasAnyChannels = Object.values(availableChannels).some(isAvailable => isAvailable);
    const hasAnyEventTypes = eventTypes.length > 0;

    if (!loading && !visibilityData?.visible) {
        return (
            <div className={cn("max-w-4xl mx-auto p-6", background.main)}>
                <ThemedCard className="p-8 text-center">
                    <Bell className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <h2 className={cn("text-xl font-semibold mb-2", text.primary)}>
                        {t('notificationsNotAvailable')}
                    </h2>
                    <p className={cn("text-sm mb-4", text.secondary)}>
                        {t('contactAdministratorToEnableNotifications')}
                    </p>
                </ThemedCard>
            </div>
        );
    }


    // Loading state
    if (loading) {
        return (
            <div className={cn("flex justify-center items-center h-64", text.secondary)}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3">{t('loading')}...</span>
            </div>
        );
    }

    // No channels or event types available for user preferences
    if (!hasAnyChannels || !hasAnyEventTypes) {
        return (
            <ThemedCard className="p-8">
                <div className={cn("text-center", text.secondary)}>
                    <Bell className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <h2 className={cn("text-xl font-semibold mb-2", text.primary)}>
                        {t('notificationPreferencesNotAvailable')}
                    </h2>
                    <p className="mb-4">
                        {!hasAnyChannels ? t('noNotificationChannelsAvailable') : t('noNotificationEventTypesAvailable')}
                    </p>
                    <p className={cn("text-sm", text.secondary)}>
                        {t('contactAdministratorToEnableNotifications')}
                    </p>
                </div>
            </ThemedCard>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className={cn("text-2xl font-bold", text.primary)}>
                    {t('notificationPreferences')}
                </h1>
                <p className={cn("text-sm mt-1", text.secondary)}>
                    {t('manageYourNotificationPreferences')}
                </p>
            </div>

            {/* User Notification Preferences */}
            <UserNotificationPreferences
                entityId={entityId}
                availableChannels={availableChannels}
                eventTypes={eventTypes}
                onSettingsChange={handleSettingsChange}
            />
        </div>
    );
};

export default NotificationSettings;
